import { useStore } from '@nanostores/react';
import { useCallback, useEffect } from 'react';
import type { SliderTabDropdownItem } from '~/components/ui/SliderTabs';
import { StripeLogo } from '~/components/ui/logos/StripeLogo';
import { MSG_INTEGRATE_STRIPE } from '~/lib/constants';
import { chatStore } from '~/lib/stores/chat';
import { projectStore } from '~/lib/stores/project';
import { stripeStore } from '~/lib/stores/stripe';
import { userStore } from '~/lib/stores/user';
import { classNames } from '~/utils/classNames';
import { invariant } from '~/utils/invariant';

export function useStripeDropdownMenuItem(): {
  item: SliderTabDropdownItem<string> | null;
  dialog: JSX.Element | null;
} {
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const showIntegrations = activeOrganization?.organizationSettings?.showIntegrations ?? true;
  const isStripeConfigured = useStore(stripeStore.isConfigured);
  const isRefreshingProducts = useStore(stripeStore.isRefreshingProducts);
  const permissions = useStore(projectStore.permissions);
  const stripePartialKey = useStore(stripeStore.partialKey);
  const mode = useStore(stripeStore.mode);
  const products = useStore(stripeStore.products);
  const selectedProducts = useStore(stripeStore.selectedProducts);
  const projectId = useStore(projectStore.projectId);

  const handleStripeClick = useCallback(() => {
    if (!isStripeConfigured) {
      chatStore.events.dispatch('sendChatMessage', { message: MSG_INTEGRATE_STRIPE, mode: 'build' });
    } else {
      stripeStore.isConfigVisible.set(true);
    }
  }, [isStripeConfigured]);

  const onClickGoToStripeDashboard = useCallback(() => {
    window.open(`https://dashboard.stripe.com/`, '_blank');
  }, []);

  const refreshProducts = useCallback(() => {
    invariant(projectId);
    stripeStore.refreshProducts(projectId);
  }, [projectId]);

  const editApiKey = useCallback(() => {
    stripeStore.openModalToEditApiKey();
  }, []);

  const editProducts = useCallback(() => {
    stripeStore.openModalToEditProducts();
  }, []);

  // refresh products when submenu would be shown and no products exist
  useEffect(() => {
    if (isStripeConfigured && products.length === 0 && projectId) {
      stripeStore.refreshProducts(projectId);
    }
  }, [isStripeConfigured, products.length, projectId]);

  if (!showIntegrations || !permissions.canEdit) {
    return { item: null, dialog: null };
  }

  // if not configured, show simple integration item
  if (!isStripeConfigured) {
    return {
      item: {
        label: (
          <div className="flex items-center gap-2">
            <StripeLogo size="xs" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium">Stripe</span>
              <span className="text-xs text-bolt-elements-text-secondary">Add payments to your project</span>
            </div>
          </div>
        ),
        value: 'stripe-integration',
        onClick: handleStripeClick,
      },
      dialog: null,
    };
  }

  // if configured, show submenu with management options
  const productAvailableCount = products.length;
  const productSelectedCount = selectedProducts.length;
  const hasProducts = productAvailableCount > 0;

  return {
    item: {
      label: (
        <div className="flex items-center gap-2">
          <StripeLogo size="xs" />
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium">Stripe</span>
            <span className="text-xs text-bolt-elements-text-secondary">Manage your Stripe integration</span>
          </div>
        </div>
      ),
      value: 'stripe-integration',
      submenu: [
        {
          type: 'content' as const,
          label: (
            <div className="flex justify-between items-center px-2 py-1.5">
              <h3 className="text-sm font-semibold">Stripe</h3>
              <button
                className={classNames(
                  'px-2 py-1 text-xs rounded bg-bolt-elements-button-secondary-background hover:bg-bolt-elements-button-secondary-backgroundHover text-bolt-elements-button-secondary-text flex items-center gap-1',
                  {
                    'opacity-50 cursor-not-allowed': isRefreshingProducts,
                  },
                )}
                disabled={isRefreshingProducts}
                onClick={refreshProducts}
              >
                {isRefreshingProducts ? (
                  <span className="i-svg-spinners:90-ring-with-bg w-3 h-3" />
                ) : (
                  <i className="i-ph:arrows-clockwise w-3 h-3" />
                )}
                Sync now
              </button>
            </div>
          ),
          value: 'stripe-header',
        },
        {
          type: 'divider' as const,
          label: '',
          value: 'divider-1',
        },
        {
          label: (
            <div className="flex flex-col gap-0.5 px-2 py-1.5 hover:bg-bolt-elements-item-backgroundActive rounded-sm cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="i-ph:key w-4 h-4" />
                  <div className="flex items-center">
                    <span className="text-sm">API key</span>
                    {mode && (
                      <div
                        className={classNames(
                          'uppercase rounded font-medium px-1 py-[3.226px] line-height-[12.903px] text-[9.677px] ml-1 shrink-0 flex items-center',
                          {
                            'bg-bolt-ds-warning-container text-bolt-ds-on-warning-container': mode === 'test',
                            'bg-bolt-ds-success-container text-bolt-ds-on-success-container': mode === 'live',
                          },
                        )}
                      >
                        {mode}
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-xs text-bolt-elements-text-secondary opacity-0 group-hover:opacity-100">
                  Edit
                </span>
              </div>
              <div className="text-xs text-bolt-elements-text-secondary ml-6">{stripePartialKey}</div>
            </div>
          ),
          value: 'edit-api-key',
          onClick: editApiKey,
        },
        {
          label: (
            <div className="flex flex-col gap-0.5 px-2 py-1.5 hover:bg-bolt-elements-item-backgroundActive rounded-sm cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="i-ph:shopping-bag w-4 h-4" />
                  <span className="text-sm">Products</span>
                </div>
                <span className="text-xs text-bolt-elements-text-secondary opacity-0 group-hover:opacity-100">
                  Edit
                </span>
              </div>
              <div className="text-xs text-bolt-elements-text-secondary ml-6">
                {hasProducts ? `${productSelectedCount} of ${productAvailableCount} activated` : 'No products found'}
              </div>
            </div>
          ),
          value: 'edit-products',
          onClick: editProducts,
        },
        {
          type: 'divider' as const,
          label: '',
          value: 'divider-2',
        },
        {
          label: (
            <div className="flex items-center gap-2">
              <StripeLogo size="xs" />
              <span>Stripe dashboard</span>
              <span className="i-ph:arrow-up-right w-4 h-4 ml-auto" />
            </div>
          ),
          value: 'go-to-stripe-dashboard',
          onClick: onClickGoToStripeDashboard,
        },
      ],
    },
    dialog: null,
  };
}
